import { Draft, produce } from 'immer';
import { DesignerParams, ProductTypesFilter } from '@/types';
import { ProductTypesActionTypes, ProductTypesActions, ProductTypesState } from './types';

const INITIAL_STATE: ProductTypesState = {
  detail: {},
  assortment: {
    categories: [],
    departments: [],
    features: [],
    appearanceGroups: [],
    sizeGroups: [],
    brands: [],
    productTypes: [],
    refinements: [],
    materials: [],
    fits: [],
    necklines: [],
    styles: [],
    sleeves: [],
    categoryPath: null,
  },
  assortmentStatus: 'initial',
  filter: {},
  ratings: {},
};

export const getInitialProductTypesState = (params: DesignerParams) =>
  produce<ProductTypesState>(INITIAL_STATE, (draft) => {
    if (
      params.productTypeCategoryId ||
      params.departmentId ||
      params.productTypeDepartmentId ||
      params.productTypeFeatureId ||
      params.productTypeAppearanceId ||
      params.productTypeSizeId ||
      params.productTypeBrandId ||
      params.productTypeMaterialsId ||
      params.productTypeFitsId ||
      params.productTypeNecklinesId ||
      params.productTypeStylesId ||
      params.productTypeSleevesId ||
      params.productTypeMaxPrice
    ) {
      const filter: ProductTypesFilter = {};
      if (params.productTypeCategoryId) {
        filter.category = params.productTypeCategoryId;
      }
      if (params.departmentId || params.productTypeDepartmentId) {
        filter.department = params.departmentId
          ? params.departmentId
          : params.productTypeDepartmentId;
      }
      if (params.productTypeFeatureId) {
        filter.feature = params.productTypeFeatureId;
      }
      if (params.productTypeAppearanceId) {
        filter.appearanceGroup = params.productTypeAppearanceId;
      }
      if (params.productTypeSizeId) {
        filter.sizeGroup = params.productTypeSizeId;
      }
      if (params.productTypeBrandId) {
        filter.brand = params.productTypeBrandId;
      }
      if (params.productTypeMaterialsId) {
        filter.material = params.productTypeMaterialsId;
      }
      if (params.productTypeFitsId) {
        filter.fit = params.productTypeFitsId;
      }
      if (params.productTypeNecklinesId) {
        filter.neckline = params.productTypeNecklinesId;
      }
      if (params.productTypeStylesId) {
        filter.style = params.productTypeStylesId;
      }
      if (params.productTypeSleevesId) {
        filter.sleeve = params.productTypeSleevesId;
      }
      if (params.productTypeMaxPrice) {
        filter.maxPrice = params.productTypeMaxPrice;
      }
      draft.filter = filter;
    }
    return draft;
  });

export const productTypesReducer = produce(
  (draft: Draft<ProductTypesState>, action: ProductTypesActions) => {
    switch (action.type) {
      case ProductTypesActionTypes.PRODUCT_TYPE_SET:
        draft.detail[action.productType.id] = action.productType;
        return draft;
      case ProductTypesActionTypes.PRODUCT_TYPES_ASSORTMENT_SET:
        const { assortment } = action;
        draft.assortment = assortment;
        draft.assortmentStatus = 'success';
        if (assortment.updatedFilters) {
          draft.filter = {
            ...draft.filter,
            ...assortment.updatedFilters,
          };
        }
        return draft;
      case ProductTypesActionTypes.PRODUCT_TYPES_ASSORTMENT_SET_STATUS:
        draft.assortmentStatus = action.status;
        return draft;
      case ProductTypesActionTypes.PRODUCT_TYPES_FILTER_SET:
        draft.filter = action.filter;
        return draft;
      case ProductTypesActionTypes.PRODUCT_TYPES_RATINGS_SET:
        draft.ratings = action.ratings;
        return draft;
      default:
        return draft;
    }
  },
  INITIAL_STATE
);
